import { atom, useAtomValue } from "jotai"
import { atomWithQuery } from "jotai-tanstack-query"
import { useCallback } from "react"
import type { SessionResponse } from "@/api/client/auth/models"
import type { AdminGet200Admin } from "@/api/generated/models"
import customInstance from "@/api/instance"

export type SessionUser = NonNullable<SessionResponse["user"]>

/**
 * セッション情報を取得するatom (jotai-tanstack-query)
 */
export const sessionQueryAtom = atomWithQuery(() => ({
  queryKey: ["get-session"],
  queryFn: () =>
    customInstance<SessionResponse>("/api/auth/get-session", {
      method: "GET",
    }),
}))

/**
 * セッションユーザー情報を取得する派生atom
 */
export const sessionUserAtom = atom<SessionUser | null>((get) => {
  const query = get(sessionQueryAtom)
  return query.data?.user ?? null
})

/**
 * セッション読み込み中かどうかを取得する派生atom
 */
export const sessionLoadingAtom = atom((get) => {
  const query = get(sessionQueryAtom)
  return query.isPending
})

/**
 * セッションエラーを取得する派生atom
 */
export const sessionErrorAtom = atom<Error | null>((get) => {
  const query = get(sessionQueryAtom)
  return query.error ?? null
})

/**
 * セッション情報が有効かどうかを取得する派生atom
 */
export const isSessionValidAtom = atom((get) => {
  const query = get(sessionQueryAtom)
  return !query.isPending && !query.error && query.data?.user != null
})

/**
 * セッション情報を再取得するフック
 */
export function useSessionRefetch() {
  const query = useAtomValue(sessionQueryAtom)
  return useCallback(() => query.refetch(), [query])
}

/**
 * 現在のadminユーザー情報を取得するatom (jotai-tanstack-query)
 */
export const adminUserQueryAtom = atomWithQuery((get) => {
  const sessionUser = get(sessionUserAtom)
  const userId = sessionUser?.id

  return {
    queryKey: ["admin", userId],
    queryFn: async () => {
      if (!userId) return null
      const response = await customInstance<{ admin: AdminGet200Admin }>(`/admins/${userId}`, {
        method: "GET",
      })
      return response.admin
    },
    enabled: !!userId,
  }
})

/**
 * 現在のadminユーザー情報を取得する派生atom
 */
export const currentAdminAtom = atom<AdminGet200Admin | null>((get) => {
  const query = get(adminUserQueryAtom)
  return query.data ?? null
})

/**
 * adminユーザー情報を更新するフック
 */
export function useRefreshAdmin() {
  const query = useAtomValue(adminUserQueryAtom)
  return useCallback(() => query.refetch(), [query])
}
